import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BillyPayerLegal,
  BillyPayerNatural,
} from 'src/app/@interfaces/Billy/billy.interface';
import { BillyService } from 'src/app/@pages/billyService/billy.service';
import { AlertService } from '@shared-services';

@Injectable({
  providedIn: 'root',
})
export class BillyPayerService {
  // ========== Dependency Injection ==========
  private readonly translate = inject(TranslateService);
  private readonly spinnerService = inject(NgxSpinnerService);
  private readonly billyService = inject(BillyService);
  private readonly alertService = inject(AlertService);

  private knownErrors = new Set([
    'An office already exists in this city',
    'The idNumber is already registered',
  ]);

  // ========== Billy/DIAN Registration Methods ==========
  sendDianLegal(customerForm: FormGroup, files: unknown): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const formData = new FormData();
      const billyPayerLegal = this.setBillyPayerLegal(customerForm, files);

      formData.append('address', billyPayerLegal.address);
      formData.append('cityId', billyPayerLegal.cityId);
      formData.append('email', billyPayerLegal.email);
      formData.append('headquarter', String(billyPayerLegal.headquarter));
      formData.append('idNumber', billyPayerLegal.idNumber);
      formData.append('idType', String(billyPayerLegal.idType));
      formData.append('name', billyPayerLegal.name);
      formData.append('phone', billyPayerLegal.phone);
      formData.append('rut', billyPayerLegal.rut);

      this.billyService.postLegalPayers(formData).subscribe({
        next: (result) => {
          if (result) {
            this.spinnerService.hide();
            this.alertService
              .open({
                title: this.translate.instant('commercial.common.success'),
                message: this.translate.instant(
                  'commercial.customers.success.sent-to-billy',
                ),
                showClose: true,
                type: 'success',
              })
              .subscribe({
                accept: () => resolve(true),
                close: () => resolve(true),
              });
          }
        },
        error: (error) => {
          const detail = error.error?.errors?.[0]?.detail;
          if (this.knownErrors.has(detail)) {
            resolve(error);
          } else {
            this.spinnerService.hide();
            this.billyService.handleError(error);
            reject(error);
          }
        },
      });
    });
  }

  sendDianNatural(customerForm: FormGroup): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const formData = new FormData();
      let billyPayerNatural: BillyPayerNatural;
      try {
        billyPayerNatural = this.setBillyPayerNatural(customerForm);
      } catch (error) {
        this.spinnerService.hide();
        reject(error);
        return;
      }

      formData.append('address', billyPayerNatural.address);
      formData.append('cityId', billyPayerNatural.cityId);
      formData.append('email', billyPayerNatural.email);
      formData.append('idNumber', billyPayerNatural.idNumber);
      formData.append('idType', String(billyPayerNatural.idType));
      formData.append('lastname', billyPayerNatural.lastname);
      formData.append('name', billyPayerNatural.name);

      this.billyService.postNaturalPayers(formData).subscribe({
        next: (result) => {
          if (result) {
            this.spinnerService.hide();
            this.alertService
              .open({
                title: this.translate.instant('commercial.common.success'),
                message: this.translate.instant(
                  'commercial.customers.success.sent-to-billy',
                ),
                showClose: true,
                type: 'success',
              })
              .subscribe({
                accept: () => resolve(true),
                close: () => resolve(true),
              });
          }
        },
        error: (error) => {
          const detail = error.error?.errors?.[0]?.detail;
          if (this.knownErrors.has(detail)) {
            resolve(error);
          } else {
            this.spinnerService.hide();
            this.billyService.handleError(error);
            reject(error);
          }
        },
      });
    });
  }

  // ========== Data Mapping Methods ==========
  private setBillyPayerLegal(form: FormGroup, files: unknown): BillyPayerLegal {
    const document = form.get('document')?.value || '';
    const dv = form.get('dv')?.value || '';
    const documentType = form.get('billyDocumentType')?.value || '';

    return {
      address: form.get('address')?.value || '',
      cityId: form.get('cityBillyData')?.value?.id || '',
      email: form.get('email')?.value || '',
      headquarter: true,
      idNumber: `${document}-${dv}`,
      idType: parseInt(documentType),
      name: form.get('name')?.value || '',
      phone: form.get('phone')?.value || '',
      rut: files as any,
    };
  }

  private setBillyPayerNatural(form: FormGroup): BillyPayerNatural {
    const fullName = form.get('name')?.value || '';
    const document = form.get('document')?.value || '';
    const documentType = form.get('billyDocumentType')?.value || '';

    const { name, lastName } = this.parseFullName(fullName);

    return {
      address: form.get('address')?.value || '',
      cityId: form.get('cityBillyData')?.value?.id || '',
      email: form.get('email')?.value || '',
      idNumber: document,
      idType: parseInt(documentType),
      name: name,
      lastname: lastName,
    };
  }

  // ========== Helper Methods ==========
  private parseFullName(fullName: string): { name: string; lastName: string } {
    const nameParts = fullName.trim().split(/\s+/);

    if (nameParts.length === 1) {
      this.alertService.open({
        title: this.translate.instant('commercial.common.error'),
        message: this.translate.instant(
          'commercial.customers.errors.invalid-name-format',
        ),
        showClose: true,
        type: 'error',
      });
      throw new Error('Invalid name format');
    }

    if (nameParts.length === 2) {
      return {
        name: nameParts[0],
        lastName: nameParts[1],
      };
    }

    if (nameParts.length === 3) {
      return {
        name: nameParts[0],
        lastName: `${nameParts[1]} ${nameParts[2]}`,
      };
    }

    return {
      name: nameParts.slice(0, nameParts.length - 2).join(' '),
      lastName: `${nameParts[nameParts.length - 2]} ${nameParts[nameParts.length - 1]}`,
    };
  }
}
